/**
 * Quyền sub-admin — danh mục ở docs/KE_HOACH_TAI_CAU_TRUC.md Phụ lục A.
 * Nhiều NPP (docs/KE_HOACH_SUPER_ADMIN_NEN_TANG.md): Super admin (User.isSuperAdmin) là tài khoản NỀN TẢNG —
 * chỉ có quyền cấp tổng (PLATFORM_ONLY); xem dữ liệu toàn hệ thống ở route /platform/*, không có quyền nào trong
 * NPP. Chủ NPP (User.isNppOwner) có mọi quyền trong NPP của mình trừ PLATFORM_ONLY; sub-admin chỉ có các dòng
 * AdminPermission được cấp (không bao giờ gồm PLATFORM_ONLY). Hàm thuần, không I/O.
 */

export const ADMIN_PERMISSIONS = [
  'dashboard.view',
  'units.view',
  'units.agency.manage',
  'units.ctv.manage',
  'units.shop.manage',
  'units.lock',
  'units.move',
  'units.type.change',
  'units.targets.manage',
  'pricing.cost.view',
  'pricing.cost.edit',
  'pricing.lists.manage',
  'pricing.assign',
  'commission.manage',
  'commission.close',
  'carrier.view',
  'carrier.manage',
  'orders.view',
  'orders.intervene',
  'tickets.view',
  'tickets.handle',
  'reconcile.view',
  'reconcile.carrier',
  'reconcile.ship_prepay',
  'payout.create',
  'payout.approve',
  'payout.pay',
  'payout.cancel',
  'debt.manage',
  'finance.writeoff',
  'finance.view',
  'settings.manage',
  'admins.manage',
  'audit.view',
  'sagas.view',
  'announce.send',
  'npps.view',
  'npps.manage',
] as const
export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number]

const ALL = new Set<string>(ADMIN_PERMISSIONS)

export function isAdminPermission(p: string): p is AdminPermission {
  return ALL.has(p)
}

/** Quyền kéo theo (P7: lập bảng giá phải thấy giá gốc để không bán dưới gốc). */
const IMPLIES: Partial<Record<AdminPermission, AdminPermission[]>> = {
  // Mọi route đơn vị (admin-api routes/shops.ts) đều cần units.view — tạo / sửa / khoá mà không xem được thì vô dụng
  'units.agency.manage': ['units.view'],
  'units.ctv.manage': ['units.view'],
  'units.shop.manage': ['units.view'],
  'units.lock': ['units.view'],
  // Chuyển nhánh / nâng cấp / chỉ tiêu nâng cấp (docs/KE_HOACH_NANG_CAP_DON_VI.md §3.4)
  'units.move': ['units.view'],
  'units.type.change': ['units.view'],
  'units.targets.manage': ['units.view'],
  'pricing.lists.manage': ['pricing.cost.view'],
  'pricing.cost.edit': ['pricing.cost.view'],
  'carrier.manage': ['carrier.view'],
  'orders.intervene': ['orders.view'],
  'tickets.handle': ['tickets.view'],
  'reconcile.carrier': ['reconcile.view'],
  'reconcile.ship_prepay': ['reconcile.view'],
  'payout.create': ['reconcile.view'],
  'payout.approve': ['reconcile.view'],
  'payout.pay': ['reconcile.view'],
  'payout.cancel': ['reconcile.view'],
  'debt.manage': ['reconcile.view'],
  'finance.writeoff': ['reconcile.view'],
  'npps.manage': ['npps.view'],
  // Gửi thông báo (docs/KE_HOACH_GUI_THONG_BAO.md): chọn nhánh / đơn vị nhận phải xem được danh sách đơn vị
  'announce.send': ['units.view'],
}

/**
 * Có trong danh mục (dòng AdminPermission cũ vẫn hợp lệ) nhưng chưa route nào kiểm: chưa có thao tác can thiệp đơn.
 * Không đưa vào bộ chọn cấp quyền để không hiểu nhầm. (`units.move` dùng từ 16/10 cho chuyển nhánh.)
 */
export const UNUSED_PERMISSIONS: readonly AdminPermission[] = ['orders.intervene']

/** Quyền hiện trong bộ chọn cấp quyền của chủ NPP / sub-admin. */
export function isGrantablePermission(p: string): boolean {
  return isAdminPermission(p) && !isPlatformOnly(p) && !(UNUSED_PERMISSIONS as readonly string[]).includes(p)
}

/** Quyền cấp tổng ShopTrack — chỉ Super Admin có sẵn; không cấp được cho ai qua AdminPermission. */
export const PLATFORM_ONLY: readonly AdminPermission[] = ['npps.view', 'npps.manage']

export function isPlatformOnly(p: string): boolean {
  return (PLATFORM_ONLY as readonly string[]).includes(p)
}

/** Người có quyền: super admin / chủ NPP / sub-admin (theo dòng AdminPermission). */
export interface PermissionHolder {
  isSuperAdmin: boolean
  isNppOwner?: boolean
  /** NPP của tài khoản (đã quy đổi: admin cũ để trống = NPP mặc định); null = Super Admin (nền tảng) */
  adminNppId?: string | null
  permissions: readonly string[]
}

/** Thêm các quyền kéo theo, bỏ trùng, giữ thứ tự danh mục. */
export function withImplied(perms: readonly string[]): AdminPermission[] {
  const out = new Set<AdminPermission>()
  for (const p of perms) {
    if (!isAdminPermission(p)) throw new Error(`Quyền không tồn tại: "${p}"`)
    out.add(p)
    for (const q of IMPLIES[p] ?? []) out.add(q)
  }
  return ADMIN_PERMISSIONS.filter((p) => out.has(p))
}

/** "units.*" → mọi quyền bắt đầu bằng "units." */
function expand(pattern: string): AdminPermission[] {
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -1)
    const hit = ADMIN_PERMISSIONS.filter((p) => p.startsWith(prefix))
    if (hit.length === 0) throw new Error(`Mẫu quyền không khớp quyền nào: "${pattern}"`)
    return hit
  }
  if (!isAdminPermission(pattern)) throw new Error(`Quyền không tồn tại: "${pattern}"`)
  return [pattern]
}

/** Mẫu nhanh ở Phụ lục A. `finance.writeoff` không nằm trong mẫu nào — chủ NPP cấp riêng. */
export const PERMISSION_TEMPLATES = {
  accountant: {
    label: 'Kế toán',
    patterns: ['dashboard.view', 'units.view', 'reconcile.*', 'payout.*', 'debt.manage', 'finance.view', 'pricing.cost.view', 'commission.close'],
  },
  operator: {
    label: 'Vận hành',
    patterns: ['dashboard.view', 'units.*', 'pricing.cost.view', 'pricing.lists.manage', 'pricing.assign', 'orders.*', 'carrier.view', 'sagas.view'],
  },
  partner_care: {
    label: 'Chăm sóc đối tác',
    patterns: ['dashboard.view', 'units.view', 'orders.view', 'reconcile.view'],
  },
} as const satisfies Record<string, { label: string; patterns: readonly string[] }>
export type PermissionTemplate = keyof typeof PERMISSION_TEMPLATES

export function templatePermissions(t: PermissionTemplate): AdminPermission[] {
  return withImplied(PERMISSION_TEMPLATES[t].patterns.flatMap(expand)).filter((p) => !(UNUSED_PERMISSIONS as readonly string[]).includes(p))
}

/**
 * Người cấp chỉ được cấp quyền mình đang có (Phụ lục A, `admins.manage`). Trả về các quyền vượt
 * phạm vi — rỗng là hợp lệ. Theo NPP (§3.5, sửa 14/10):
 *  1. quyền PLATFORM_ONLY không cấp được cho ai (chỉ Super Admin có sẵn);
 *  2. Super Admin không cấp quyền trong NPP — việc đó của chủ NPP;
 *  3. khi biết người nhận (`target`), người cấp và người nhận phải cùng NPP (`adminNppId` đã quy đổi);
 *  4. chủ NPP cấp được mọi quyền ngoài PLATFORM_ONLY cho admin trong NPP mình.
 */
export function grantBeyondScope(
  grantor: PermissionHolder,
  requested: readonly string[],
  target?: { adminNppId: string | null },
): AdminPermission[] {
  const wanted = withImplied(requested)
  if (grantor.isSuperAdmin) return wanted
  if (target && (grantor.adminNppId ?? null) !== target.adminNppId) return wanted
  const platformOnly = wanted.filter(isPlatformOnly)
  if (grantor.isNppOwner) return platformOnly
  const own = new Set(grantor.permissions)
  if (!own.has('admins.manage')) return wanted
  return [...new Set([...platformOnly, ...wanted.filter((p) => !own.has(p))])]
}

export function hasPermission(user: PermissionHolder, p: AdminPermission): boolean {
  // Super Admin = nền tảng: chỉ quyền cấp tổng; mọi quyền trong NPP thuộc chủ NPP / sub-admin của NPP
  if (user.isSuperAdmin) return isPlatformOnly(p)
  if (isPlatformOnly(p)) return false
  if (user.isNppOwner) return true
  return user.permissions.includes(p)
}
